import calculateFilledPercentage from "utils/calculateFilledPercentage";
import axiosInstance from "../../config/axios";
// import transformSaveProfileData from '@/middlewares/transform/saveProfileData';
import { transformUser } from "../../middlewares/transform/transformerLogin";
import { User } from "../../models/User";
import AppStorage from "../../services/AppStorage";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setCookie } from "utils/StreamingHelperFunctions";

interface Token {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
}

interface SaveProfileDataParams {
  firstName: string;
  lastName: string;
  ageGroup: string;
  industry: string;
  organization: string;
  designation: string;
  city: string;
  country: string;
  aboutMe: string;
  website: string;
  profileMessage: string;
  linkedinLink: string;
}

interface AuthState {
  token: Token | null;
  user: null | User;
  isLoading: boolean;
  error: null | string;
  profilePhoto?: string;
  profilePercentage?: number;
  profileData?: any;


}

const initialAuthState: AuthState = {
  isLoading: false,
  token: null,
  user: null,
  error: null,
  profilePhoto: null,
  profilePercentage: null,
  profileData: null
};

const _storage = new AppStorage();

export const login = createAsyncThunk(
  "auth/login",
  async (
    { username, password }: { username: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post("/trainee/login", {
        trainee_email: username,
        trainee_password: password,
      });
      const { token, user } = response.data;
      setCookie("access-token", token?.accessToken, 24);
      const transformedUser = transformUser(user);
      return { token, user: transformedUser };
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  }
);

export const signup = createAsyncThunk(
  "auth/signup",
  async (
    {
      firstName,
      lastName,
      email,
      password,
    }: { firstName: string; lastName: string; email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await axiosInstance.post(
        "/trainee/sign_up_with_email",
        {
          trainee_name: `${firstName} ${lastName}`,
          first_name: firstName,
          last_name: lastName,
          trainee_email: email,
          trainee_password: password,
          joineByCommonLink: true,
        }
      );
      if (response.error) {
        return rejectWithValue(response.message);
      }
      const { token, user } = response.data;
      const transformedUser = transformUser(user);
      return { token, user: transformedUser };
    } catch (error: any) {
      if (error.response?.data?.message === "User already registered.") { // temp fix of toast message
        return rejectWithValue("User already registered.");
      } else {
        return rejectWithValue(error.response?.message || "Signup failed");
      }
    }
  }
);
export const getProfileData = createAsyncThunk('/trainee/get_trainee_profile_data', async () => {
  try {
    const profileData: any = await axiosInstance.get('/trainee/get_trainee_profile_data');
    return transformUser(profileData.data.userData);
    // return profileData.data.userData;

  } catch (error) {
    throw error
  }
})

export const saveProfileData = createAsyncThunk(
  "auth/saveProfileData",
  async (params: SaveProfileDataParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/trainee/save_trainee_profile_data",
        {
          first_name: params.firstName,
          last_name: params.lastName,
          age_group: params.ageGroup,
          industry: params.industry,
          organization: params.organization,
          role: params.designation,
          town_city: params.city,
          country: params.country,
          website: params.website,
          short_bio: params.profileMessage,
          aboutMe: params.aboutMe,
          linkedinLink: params.linkedinLink,
        }
      );

      console.log(response);

      const profileData = {};
      return profileData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const loginWithOtp = createAsyncThunk(
  "auth/loginWithOtp",
  async ({ username }: { username: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/trainee/login", {
        trainee_email: username,
        loginWithOtp: true,
      });
      // This endpoint sends OTP but doesn't return token/user yet
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to send OTP");
    }
  }
);

export const verifyOtpAndLogin = createAsyncThunk(
  "auth/verifyOtpAndLogin",
  async ({ email, otp }: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response: any = await axiosInstance.post("/trainee/verify_email", {
        email: email,
        otp: otp,
      });

      // Handle the nested response structure: response.data.user.trainee
      const { token, user, success, message } = response;
      if (!success) {
        return rejectWithValue(message || "OTP verification failed");
      }

      // Transform the nested trainee data
      const traineeData = user?.trainee || user;
      const transformedUser = transformUser(traineeData);

      return { token, user: transformedUser };
    } catch (error: any) {
      return rejectWithValue(error.response?.message || "Invalid OTP");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: initialAuthState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.user = null;
      window.localStorage.clear();
    },
    updateProfilePhoto: (
      state,
      action: PayloadAction<Partial<any>>
    ) => {
      state.profilePhoto = action.payload.profilePhoto
    },
    updateProfilePercentage: (
      state,
      action: PayloadAction<Partial<any>>
    ) => {
      state.profilePercentage = action.payload.profilePercentage
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        localStorage.setItem("token", action.payload.token.accessToken);
        state.user = action.payload.user;
        state.profilePhoto = action.payload.user.profileImage;
      })
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.user = null;
        state.token = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.error = null;
        state.isLoading = false;
        state.token = null;
        // localStorage.setItem("token", action.payload.token.accessToken);
        state.user = action.payload.user;
      })
      .addCase(signup.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(loginWithOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithOtp.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(loginWithOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyOtpAndLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOtpAndLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        localStorage.setItem("token", action.payload.token.accessToken);
        state.user = action.payload.user;
        state.profilePhoto = action.payload.user.profileImage;
      })
      .addCase(verifyOtpAndLogin.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getProfileData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileData = action.payload;
        state.profilePercentage = calculateFilledPercentage(state.profileData);
      });
  },
});

export const { logout, updateProfilePhoto, updateProfilePercentage } = authSlice.actions;
export default authSlice.reducer;
